import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useScreenSize } from '@/hooks';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import styles from './AuthModal.module.css';

/**
 * AuthModal Component
 * Custom full-screen modal for login and signup
 * Opens via route query params: /login?modalType=auth-signin or /login?modalType=auth-signup
 * Full screen on mobile and desktop
 */
const AuthModal = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const modalType = searchParams.get('modalType');
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('signin'); // 'signin' or 'signup'
  const { isMobile } = useScreenSize();

  useEffect(() => {
    // Check if modal should be open based on query params
    if (modalType === 'auth-signin' || modalType === 'auth-signup') {
      setIsOpen(true);
      setActiveTab(modalType === 'auth-signin' ? 'signin' : 'signup');
      // Prevent body scroll when modal is open - use class instead of inline style
      document.body.classList.add('modal-open');
    } else {
      setIsOpen(false);
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [modalType]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        document.body.classList.remove('modal-open');
        navigate(-1);
      }
    };

    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, navigate]);

  const handleClose = () => {
    setIsOpen(false);
    document.body.classList.remove('modal-open');
    // Remove query params and go back when modal closes
    navigate(-1);
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    const newModalType = tab === 'signin' ? 'auth-signin' : 'auth-signup';
    navigate(`/login?modalType=${newModalType}`, { replace: true });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div 
        className={`${styles.modalContainer} ${isMobile ? styles.mobileFullScreen : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button - Absolute positioned for desktop, in header for mobile */}
        <button 
          className={styles.closeButton} 
          onClick={handleClose}
          aria-label="Close modal"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className={styles.modalContent}>
          {/* Left Side - Form Section */}
          <div className={styles.formSection}>
            {/* Header with Logo and Close Button (mobile) */}
            <div className={styles.headerRow}>
              <div className={styles.logoContainer}>
                <img
                  src="/assets/images/logo/web-logo.webp"
                  alt="JACKOPOT"
                  className={styles.logo}
                  onError={(e) => {
                    e.target.src = '/assets/images/lg.png';
                  }}
                />
              </div>
              <button 
                className={styles.closeButtonMobile} 
                onClick={handleClose}
                aria-label="Close modal"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Form Content */}
            <div className={styles.formContainer}>
              {activeTab === 'signin' ? (
                <LoginForm onSwitchToSignup={() => switchTab('signup')} />
              ) : (
                <RegisterForm onSwitchToSignin={() => switchTab('signin')} />
              )}
            </div>
          </div>

          {/* Right Side - Background Image Section */}
          {!isMobile && (
            <div className={styles.imageSection}>
              <div 
                className={styles.backgroundImage}
                style={{ backgroundImage: 'url(/assets/images/auth-bg.webp)' }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
